/**
 * Ocean prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const oceanPrintsPack: ToilePack = {
    slug: 'ocean-prints',
    category: 'wall-art',
    name: 'Ocean prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'wave', label: 'Wave', subject: 'a breaking wave seen from directly above, turquoise water curling into white foam over pale sand'},
        {key: 'shoreline', label: 'Shoreline', subject: 'a long empty beach at low tide, wet sand mirroring a pale sky, a low headland and a thin line of dune grass'},
        {key: 'whale', label: 'Whale tail', subject: "a humpback whale's tail lifting clear of calm water at sunset, spray falling from its trailing edge, the horizon low and empty"},
    ],
    styles: [
        {key: 'wash', label: 'Watercolor', style: 'a loose watercolor wash with visible paper grain, soft bleeding edges and dry-brush foam, no outlines', palette: 'The color lives in turquoise, sea-glass green and warm sand beige on white paper', framing: 'Centered with the wash fading to bare paper at the edges and a wide margin all round'},
        {key: 'cut', label: 'Flat cut', style: 'flat vector shapes with bold clean edges, no outlines and smooth gradient-free fills', palette: 'A limited palette of five colors: deep teal, foam cream, mustard sun, coral and off-white', framing: 'Full-bleed across the whole sheet with the horizon set high'},
        {key: 'block', label: 'Block print', style: 'a carved block print with visible woodgrain, hand-cut shapes and slight ink misregistration', palette: 'Two-color ink, indigo and pale foam white on cream paper', framing: 'Centered inside a thin keyline border with an even margin'},
    ],
    directions: ['in stormy grey instead', 'as a set of three', 'for a bathroom wall'],
    seo: {
        title: 'Ocean wall art: wave, shoreline, whale tail',
        metaDescription: 'Nine ocean prints — a wave, a shoreline and a whale tail — in watercolor, flat cut or block-print style, sea-glass palette, the beach-house print.',
        h1: 'Ocean wall art: wave, shoreline and whale tail',
        tagline: 'Nine prints, three coastal subjects, three finishes each — sea-glass palette, painterly or clean flat.',
        intro: "The coastal wall in nine prints: a wave from above in turquoise and foam, a long empty shoreline at low tide, a whale's tail at sunset. These nine cross those three subjects with a soft watercolor wash, a clean flat-cut treatment and a textured block-print finish — the print for the bathroom, the beach house, the office that wants a window.",
        useCases: [
            'a bathroom or beach-house wall',
            'an office that wants a window',
            'a coastal-styled bedroom',
            'a set of three for a hallway',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each print is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Is this a photograph or an illustration?', a: 'Illustration — painted or flat-color artwork in the coastal-print tradition, not a photograph.'},
            {q: 'Can I ask for a stormier mood instead?', a: 'Yes — the direction field takes that request, for instance stormy grey, and redraws it in the same three finishes.'},
        ],
    },
}
